import React, { useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  IconButton,
  InputBase,
  CircularProgress
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import SendIcon from "@material-ui/icons/Send";
import CancelIcon from "@material-ui/icons/Cancel";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    background: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%"
    }
  },
  newMessageBox: {
    background: theme.palette.type === "dark" ? "#424242" : "#eee",
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center"
  },
  messageInputWrapper: {
    padding: 6,
    background: theme.palette.type === "dark" ? "#4f4e4e" : "#fff",
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative",
    minWidth: 0
  },
  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none"
  },
  sendMessageIcons: {
    color: "grey"
  },
  uploadInput: {
    display: "none"
  },
  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)"
  },
  circleLoading: {
    color: theme.palette.primary.main,
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12
  }
}));

const InternalMessageInput = ({ chatId }) => {
  const classes = useStyles();
  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();

  const handleChangeMedias = (e) => {
    if (!e.target.files) return;
    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  const handleInputPaste = (e) => {
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async (e) => {
    setLoading(true);
    if (e && e.preventDefault) e.preventDefault();

    const formData = new FormData();
    medias.forEach((media) => {
      formData.append("medias", media);
      formData.append("body", media.name);
    });

    try {
      await api.post(`/internal-chats/${chatId}/messages`, formData);
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
    setMedias([]);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    setLoading(true);

    const payload = {
      body: inputMessage.trim()
    };

    try {
      await api.post(`/internal-chats/${chatId}/messages`, payload);
    } catch (err) {
      toastError(err);
    }

    setInputMessage("");
    setLoading(false);
  };

  if (medias.length > 0) {
    return (
      <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
        <IconButton
          aria-label="cancel-upload"
          component="span"
          onClick={() => setMedias([])}
        >
          <CancelIcon className={classes.sendMessageIcons} />
        </IconButton>

        {loading ? (
          <div>
            <CircularProgress className={classes.circleLoading} />
          </div>
        ) : (
          <span>{medias[0]?.name}</span>
        )}

        <IconButton
          aria-label="send-upload"
          component="span"
          onClick={handleUploadMedia}
          disabled={loading}
        >
          <SendIcon className={classes.sendMessageIcons} />
        </IconButton>
      </Paper>
    );
  }

  return (
    <Paper square elevation={0} className={classes.mainWrapper}>
      <div className={classes.newMessageBox}>
        <input
          multiple
          type="file"
          id="internal-upload-button"
          disabled={loading}
          className={classes.uploadInput}
          onChange={handleChangeMedias}
        />
        <label htmlFor="internal-upload-button">
          <IconButton
            aria-label="upload"
            component="span"
            disabled={loading}
          >
            <AttachFileIcon className={classes.sendMessageIcons} />
          </IconButton>
        </label>

        <div className={classes.messageInputWrapper}>
          <InputBase
            inputRef={(input) => {
              if (input) {
                inputRef.current = input;
              }
            }}
            className={classes.messageInput}
            placeholder={i18n.t("internalChat.input.placeholder")}
            multiline
            maxRows={5}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            disabled={loading}
            onPaste={handleInputPaste}
            onKeyDown={(e) => {
              if (loading) return;
              if (e.key === "Enter" && e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />
        </div>

        <IconButton
          aria-label="sendMessage"
          component="span"
          onClick={handleSendMessage}
          disabled={loading}
        >
          <SendIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    </Paper>
  );
};

export default InternalMessageInput;

